import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../../services";
import _ from "lodash";
import Empty from "../../../components/elements/empty";
import AddressCard from "../../../components/cards/address-card";
import Loader from "../../../components/cards/loader";
import { useParams } from "react-router-dom";
import { Pagination, PaginationProps, Modal, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import AddAddress from "./add-address";

export default function Address(props: any) {
  const params = useParams();
  const [api, contextHolder] = notification.useNotification();
  const [innerTab, setInnerTab] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [delivery, setDelivery] = useState<any[] | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 9;
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [addressToDelete, setAddressToDelete] = useState<any>(null);

  const getAddress = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DataProvider.getDelivery();
      const data = _.sortBy(res.data, "label");
      setDelivery(data);
      setCurrentPage(1);
    } catch (error) {
      api["error"]({
        message: "Failed",
        description: "Sorry, we couldn't load your addresses. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (params.id === "4" && innerTab === null) {
      getAddress();
    }
  }, [params, innerTab, getAddress]);

  const onChange: PaginationProps["onChange"] = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteAddress = async (address: any) => {
    setDeleteModalVisible(true);
    setAddressToDelete(address);
  };

  const cancelDeleteAddress = () => {
    setDeleteModalVisible(false);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await DataProvider.deleteDelivery(addressToDelete.id);
      await getAddress();
      setDeleteModalVisible(false);
    } catch (error) {
      api["error"]({
        message: "Failed",
        description:
          "Sorry, we couldn't delete your address. Please try again.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white w-full min-h-[65vh] rounded-lg border border-solid border-gray-200 py-4 px-4 sm:px-5">
      {innerTab === null && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                Delivery addresses
              </h2>
              <p className="text-sm text-neutral-500 mt-0.5">
                Manage where your medication orders get delivered.
              </p>
            </div>
            {delivery && delivery.length > 0 && (
              <button
                type="button"
                onClick={() => setInnerTab(0)}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#06509E] text-white text-sm font-medium hover:bg-[#053d7a] transition-colors shrink-0"
              >
                <PlusOutlined />
                <span className="hidden sm:inline">Add address</span>
              </button>
            )}
          </div>

          {loading && (delivery === null || delivery.length < 1) ? (
            <div className="flex justify-center items-center py-16">
              <Loader num={1} />
            </div>
          ) : delivery && delivery.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {delivery
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map((item, idx) => (
                    <AddressCard
                      item={item}
                      key={item.id ?? idx}
                      onDelete={() => handleDeleteAddress(item)}
                    />
                  ))}
              </div>
              {delivery.length > pageSize && (
                <div className="w-full flex justify-center mt-5">
                  <Pagination
                    current={currentPage}
                    onChange={onChange}
                    total={delivery.length}
                    pageSize={pageSize}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="">
              <Empty
                imageStyle="md:h-44 h-24"
                titleStyle="md:text-lg text-sm"
                desStyle="md:text-base"
                title="No Address"
                buttons={[
                  {
                    name: "Add Address",
                    type: "primary",
                    event: () => setInnerTab(0),
                  },
                  {
                    name: "Refresh",
                    event: () => getAddress(),
                  },
                ]}
                description="You have not added a delivery address yet."
              />
            </div>
          )}
        </div>
      )}

      {innerTab === 0 && <AddAddress setInnerTab={setInnerTab} />}

      {/* Delete confirmation modal */}
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={confirmDeleteAddress}
        onCancel={cancelDeleteAddress}
        confirmLoading={isDeleting}
        okText={isDeleting ? "Loading..." : "OK"} // Change OK text to Loading when isDeleting is true
      >
        <p>Are you sure you want to delete this address?</p>
      </Modal>
      {contextHolder}
    </div>
  );
}
